// apps/client/src/pages/reports/TrailBookValuationPage.tsx
// TRAIL BOOK VALUATION REPORT - Shows trail income and valuation by entity/template

import React, { useState, useMemo, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection } from '../../utils/loanBookResolution';
import { Share2, X, Copy, Check, Loader2, BookOpen, TrendingUp, Calculator } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import StatCard from '../../components/common/StatCard';
import { PageHeader } from '../../components/common/PageHeader';
import { formatCurrency, formatCurrencyShorthand } from '../../utils/formatters';
import { COMMISSION_GST_BASIS } from '../../utils/commissionDisplay';
import { useTrailBookValuation } from '../../hooks/useTrailBookValuation';
import { useCompanyDataYearRange } from '../../hooks/useCompanyDataYearRange';

// Share Modal Component
interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string | null;
  isLoading: boolean;
  error: string | null;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, shareUrl, isLoading, error }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-bold text-white">Share Valuation Report</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <span className="ml-3 text-gray-300">Generating share link...</span>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg mb-4">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {shareUrl && !isLoading && (
          <>
            <p className="text-gray-300 mb-4">
              Share this link to give read-only access to this valuation report.
              The link includes a snapshot of the current data.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm font-mono"
              />
              <button
                onClick={handleCopy}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white flex items-center gap-2 transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-sm text-gray-500">
              This link expires in 30 days.
            </p>
          </>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

interface Office {
  id: string;
  name: string;
}

const TrailBookValuationPage: React.FC = () => {
  const { userData, companyData, userVisibleOffices, loanBooks = [] } = useOutletContext<{
    userData: any;
    companyData: any;
    userVisibleOffices: Office[];
    loanBooks: ResolvedLoanBook[];
  }>();

  // ✅ DYNAMIC YEAR RANGE: Fetch from BigQuery for year dropdown population
  const { earliestYear, latestYear } = useCompanyDataYearRange({
    companyId: userData?.companyId,
  });

  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    yearType: 'calendar' as 'calendar' | 'financial',
  });

  // Office + loan book filters
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [selectedLoanBook, setSelectedLoanBook] = useState<'all' | string>('all');

  const resolvedParams = useMemo(() =>
    resolveLoanBookSelection(selectedLoanBook, loanBooks),
    [selectedLoanBook, loanBooks]
  );

  // Reset loan book when office changes
  useEffect(() => { setSelectedLoanBook('all'); }, [selectedOffice]);

  // Filter loan books to selected office
  const filteredLoanBooks = useMemo(() => {
    const enabled = loanBooks.filter(lb => lb.isEnabled);
    if (selectedOffice === 'all') return enabled;
    return enabled.filter(lb => lb.officeId === selectedOffice);
  }, [loanBooks, selectedOffice]);

  // Multiplier for present value calculation (1x to 6x)
  const [multiplier, setMultiplier] = useState(6);

  // Share modal state
  const [shareModal, setShareModal] = useState<{
    isOpen: boolean;
    shareUrl: string | null;
    isLoading: boolean;
    error: string | null;
  }>({
    isOpen: false,
    shareUrl: null,
    isLoading: false,
    error: null,
  });

  // Generate year options for dropdown (newest first)
  // ✅ FIX (2026-01-05): Always include current year even if no data exists yet
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const dynamicLatestYear = Math.max(latestYear ?? currentYear, currentYear);
    const dynamicEarliestYear = earliestYear ?? (currentYear - 4);
    const years = [];
    for (let year = dynamicLatestYear; year >= dynamicEarliestYear; year--) {
      years.push(year);
    }
    return years;
  }, [earliestYear, latestYear]);

  // Fetch trail book valuation data
  const {
    templates,
    loading,
    error,
    totalTrailIncome,
    monthsWithData,
  } = useTrailBookValuation({
    companyId: userData?.companyId || '',
    year: filters.year,
    yearType: filters.yearType,
    officeId: selectedOffice === 'all' ? undefined : selectedOffice,
    entityCode: resolvedParams.entityCode,
  });

  // Calculate present value based on selected multiplier
  const totalPresentValue = totalTrailIncome * multiplier;

  // Handle share button click
  const handleShare = async () => {
    setShareModal({ isOpen: true, shareUrl: null, isLoading: true, error: null });

    try {
      const generateShareTokenFunc = httpsCallable<
        { companyId: string; year: number; yearType: 'calendar' | 'financial'; multiplier: number; expiresInDays?: number },
        { shareId: string; token: string; shareUrl: string }
      >(functions, 'generateTrailBookShareToken');

      const response = await generateShareTokenFunc({
        companyId: userData?.companyId,
        year: filters.year,
        yearType: filters.yearType,
        multiplier,
        expiresInDays: 30,
      });

      setShareModal({
        isOpen: true,
        shareUrl: response.data.shareUrl,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      console.error('Failed to generate share link:', err);
      setShareModal({
        isOpen: true,
        shareUrl: null,
        isLoading: false,
        error: err.message || 'Failed to generate share link. Please try again.',
      });
    }
  };

  const closeShareModal = () => {
    setShareModal({ isOpen: false, shareUrl: null, isLoading: false, error: null });
  };

  if (loading) {
    return <div className="p-8 text-center text-white">Loading trail book valuation...</div>;
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-500">
        Error loading trail book valuation: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={BookOpen}
        title="Trail Book Valuation"
        subtitle="Annual trail income and valuation by loan book"
        actions={
          <button
            onClick={handleShare}
            disabled={shareModal.isLoading || loading}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium border border-gray-600/50 text-gray-400 hover:text-white hover:bg-white/[0.04] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {shareModal.isLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Share2 className="w-3.5 h-3.5" />
            )}
            <span>Share</span>
          </button>
        }
      />

      {/* Filter Controls */}
      <div className="flex flex-wrap gap-4 mb-8">
        {/* Year Selector */}
        <div className="flex flex-col">
          <label className="text-sm text-gray-400 mb-2">Year</label>
          <select
            className="bg-gray-800 text-white px-4 py-2 rounded border border-gray-700"
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Year Type Selector */}
        <div className="flex flex-col">
          <label className="text-sm text-gray-400 mb-2">Year Type</label>
          <div className="flex gap-2">
            <button
              className={`px-4 py-2 rounded ${
                filters.yearType === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400'
              }`}
              onClick={() => setFilters({ ...filters, yearType: 'calendar' })}
            >
              Calendar Year
            </button>
            <button
              className={`px-4 py-2 rounded ${
                filters.yearType === 'financial'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400'
              }`}
              onClick={() => setFilters({ ...filters, yearType: 'financial' })}
            >
              Financial Year
            </button>
          </div>
        </div>

        {/* Office Filter - multi-office companies only, non-broker users */}
        {userData?.role !== 'broker' && userVisibleOffices && userVisibleOffices.length > 1 && (
          <div className="flex flex-col">
            <label className="text-sm text-gray-400 mb-2">Office</label>
            <select
              className="bg-gray-800 text-white px-4 py-2 rounded border border-gray-700"
              value={selectedOffice}
              onChange={(e) => setSelectedOffice(e.target.value)}
            >
              <option value="all">All Offices</option>
              {userVisibleOffices.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Loan Book Filter - unified-mode companies with loan books */}
        {filteredLoanBooks.length > 0 && (
          <div className="flex flex-col">
            <label className="text-sm text-gray-400 mb-2">Loan Book</label>
            <select
              className="bg-gray-800 text-white px-4 py-2 rounded border border-gray-700"
              value={selectedLoanBook}
              onChange={(e) => setSelectedLoanBook(e.target.value)}
            >
              <option value="all">All Loan Books</option>
              {filteredLoanBooks.map((lb) => (
                <option key={lb.id} value={lb.id}>{lb.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Multiplier Slider */}
        <div className="flex flex-col">
          <label className="text-sm text-gray-400 mb-2">Valuation Multiplier</label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="1"
              max="6"
              step="1"
              value={multiplier}
              onChange={(e) => setMultiplier(parseInt(e.target.value))}
              className="w-40 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <span className="text-xl font-bold text-white min-w-[3rem]">{multiplier}x</span>
          </div>
          <div className="flex justify-between w-40 text-xs text-gray-500 mt-1">
            <span>1x</span>
            <span>2x</span>
            <span>3x</span>
            <span>4x</span>
            <span>5x</span>
            <span>6x</span>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <StatCard
          title="Total Trail Income (Annualised)"
          value={formatCurrencyShorthand(totalTrailIncome)}
          subtitle={`Based on ${monthsWithData} month${monthsWithData !== 1 ? 's' : ''} of data${monthsWithData < 12 ? ', extrapolated to 12 months' : ''} • ${COMMISSION_GST_BASIS}`}
          icon={<TrendingUp size={24} />}
        />
        <StatCard
          title={`Total Present Value (${multiplier}x)`}
          value={formatCurrencyShorthand(totalPresentValue)}
          subtitle={`${multiplier}x multiplier valuation • ${COMMISSION_GST_BASIS}`}
          icon={<Calculator size={24} />}
        />
      </div>

      {/* Valuation Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-900">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">
                Trail Book
              </th>
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">
                Trail Income (Annualised)
              </th>
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">
                Present Value ({multiplier}x)
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {templates.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-6 py-8 text-center text-gray-400">
                  No trail income data found for {filters.year} {filters.yearType} year
                </td>
              </tr>
            ) : (
              <>
                {templates.map((template, index) => (
                  <tr
                    key={template.templateId || index}
                    className="hover:bg-gray-750 transition-colors"
                  >
                    <td className="px-6 py-4 text-white font-medium">
                      {template.entityName || template.templateName || template.templateId || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 text-right text-white">
                      {formatCurrency(template.trailIncomeAnnualisedCents)}
                    </td>
                    <td className="px-6 py-4 text-right text-white font-semibold">
                      {formatCurrency(template.trailIncomeAnnualisedCents * multiplier)}
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-gray-900 font-bold">
                  <td className="px-6 py-4 text-white">Total</td>
                  <td className="px-6 py-4 text-right text-white">
                    {formatCurrency(totalTrailIncome)}
                  </td>
                  <td className="px-6 py-4 text-right text-white">
                    {formatCurrency(totalPresentValue)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Methodology Note */}
      <div className="mt-8 p-4 bg-gray-800 rounded-lg border border-gray-700">
        <h3 className="text-sm font-semibold text-gray-300 mb-2">Valuation Methodology</h3>
        <p className="text-sm text-gray-400">
          This report calculates the present value of trail commission income using a configurable multiplier.
          Trail Income (Annualised) is extrapolated from {monthsWithData} month{monthsWithData !== 1 ? 's' : ''} of actual data to a full 12-month period{monthsWithData < 12 ? ` (factor: ${(12 / monthsWithData).toFixed(2)}x)` : ''}.
          Present Value ({multiplier}x) is calculated as Annualised Trail Income × {multiplier}. Industry standard multipliers typically range from 4x to 6x.
        </p>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModal.isOpen}
        onClose={closeShareModal}
        shareUrl={shareModal.shareUrl}
        isLoading={shareModal.isLoading}
        error={shareModal.error}
      />
    </div>
  );
};

export default TrailBookValuationPage;
